/* @flow */
import * as React from 'react';
import ReactModal from 'react-modal';
import Row from 'components/Row';
import Link from 'components/Link';
import Menu from 'components/Menu';
import Text from 'components/Text';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import Heading from 'components/Heading';
import MenuItem from 'components/MenuItem';
import MenuButton from 'components/MenuButton';
import MediaObject from 'components/MediaObject';
import AdminIntegrationsContainer from 'admin/containers/AdminIntegrationsContainer';
import AdminIntegratedToolsUpsellModal from 'admin/components/AdminIntegratedToolsUpsellModal';
import OrganizationIntegrationPermissions from 'admin/components/OrganizationIntegrationPermissions';

import './styles/AdminIntegratedToolsView.scss';

function Integrated() {
  return <span className="AdminIntegratedToolsView-integrated">Integrated</span>;
}

type AdminIntegrationProps = {
  onActionUnauthorized: Function,
  organizationUUID: string,
  canEdit: boolean,
  integration: any,
};

const AdminIntegration = (props: AdminIntegrationProps) => {
  const { isACIIntegrated } = props.integration;

  const isLTIIntegrated = props.integration.LTI != null;
  const ACIRoute = `/install_aci/${props.organizationUUID}/${props.integration.providerKey}`;
  const LTIRoute = `/install_lti/${props.organizationUUID}/${props.integration.providerKey}`;

  const actions = [];

  const ACIButtonProps = props.canEdit ? { to: ACIRoute } : { onClick: props.onActionUnauthorized };
  const LTIButtonProps = props.canEdit ? { to: LTIRoute } : { onClick: props.onActionUnauthorized };

  if (!isACIIntegrated) {
    actions.push(
      <Button key="aci" size="xsmall" level="primary" {...ACIButtonProps}>
        Set up {props.integration.title} app
      </Button>
    );
  } else if (!isLTIIntegrated) {
    actions.push(
      <Button key="lti" size="xsmall" level={'primary'} {...LTIButtonProps}>
        Set up LTI
      </Button>
    );
  }

  if (isACIIntegrated || isLTIIntegrated) {
    actions.push(
      <MenuButton
        key="menu"
        size="xsmall"
        level="secondary"
        alignment="right"
        placement="bottom"
        menu={
          <Menu>
            {isACIIntegrated && <MenuItem {...ACIButtonProps}>Manage app</MenuItem>}
            {isLTIIntegrated && <MenuItem {...LTIButtonProps}>Manage LTI</MenuItem>}
          </Menu>
        }>
        <Glyph name="ellipses" solo />
      </MenuButton>
    );
  }

  return (
    <div className="AdminIntegratedToolsView-admin">
      <div className="AdminIntegratedToolsView-admin-info">
        <MediaObject gutter="medium" center>
          <div className="AdminIntegratedToolsView-admin-info-logo">
            <Image src={props.integration.iconURL} />
          </div>
          <div>
            <div>
              <Text weight="semibold" size="medium">
                {props.integration.title}
              </Text>
              {isACIIntegrated && (
                <Text size="xsmall">
                  <Integrated />
                </Text>
              )}
            </div>
            <Text element="div" color="thunder">
              {props.integration.productShortDescription}
            </Text>
          </div>
        </MediaObject>
      </div>
      <div className="AdminIntegratedToolsView-admin-buttons">{actions}</div>
    </div>
  );
};

type PartnerIntegrationProps = {
  organizationUUID: string,
  onClick: Function,
  integration: any,
};

const PartnerIntegration = (props: PartnerIntegrationProps) => {
  return (
    <Link onClick={props.onClick} color="inherit">
      <Row size="small">
        <MediaObject center gutter="small">
          <div className="AdminIntegratedToolsView-partner-logo">
            <Image src={props.integration.iconURL} />
          </div>
          <div>{props.integration.title}</div>
        </MediaObject>
      </Row>
    </Link>
  );
};

type PartnerIntegrationModalProps = {
  integration: any,
  onRequestHide: Function,
};

const PartnerIntegrationModal = (props: PartnerIntegrationModalProps) => {
  const image = (() => {
    // prettier-ignore
    switch (props.integration.providerKey) {
      case 'canvas':             return require('assets/images/aci/canvas.png');
      case 'quizlet':            return require('assets/images/aci/quizlet.png');
      case 'schoology':          return require('assets/images/aci/schoology.png');
      case 'google_drive':       return require('assets/images/aci/google_drive.png');
      case 'google_classroom':   return require('assets/images/aci/google_classroom.png');
      case 'microsoft_onedrive': return require('assets/images/aci/onedrive.png');
      default:                   return require('assets/images/aci/canvas.png');
    }
  })();

  return (
    <Dialog onRequestHide={props.onRequestHide} className="AdminIntegratedToolsView-partnerModal">
      <div className="AdminIntegratedToolsView-partnerModal-hero">
        <Image src={image} />
      </div>
      <div className="AdminIntegratedToolsView-partnerModal-content">
        <div className="AdminIntegratedToolsView-partnerModal-content-logoContainer">
          <div className="AdminIntegratedToolsView-partnerModal-content-logoContainer-logo">
            <Image src={props.integration.iconURL} />
          </div>
        </div>
        <div className="AdminIntegratedToolsView-partnerModal-content-status">
          <Text color="koala" size="xsmall">
            Enabled for your organization!
          </Text>
        </div>
        <Heading levelStyle="1">{props.integration.title}</Heading>
        <Text>{props.integration.productLongDescription}</Text>
      </div>
    </Dialog>
  );
};

type Props = {
  data: any,
  organizationUUID: string,
};

type State = {
  showPartnerIntegration?: *,
  showAdminControlledIntegrationUpsell?: *,
};

class AdminIntegratedToolsView extends React.Component<Props, State> {
  state: State = {};

  showUpsellModal = (int: *) => this.setState({ showAdminControlledIntegrationUpsell: int });
  closeUpsellModal = () => this.setState({ showAdminControlledIntegrationUpsell: undefined });

  showPartnerModal = (int: *) => this.setState({ showPartnerIntegration: int });
  closePartnerModal = () => this.setState({ showPartnerIntegration: undefined });

  render() {
    return (
      <OrganizationIntegrationPermissions organizationUUID={this.props.organizationUUID}>
        {({ canSee, canEdit, loading }) => {
          if (loading) {
            return null;
          }

          if (!canSee) {
            return <div>not found</div>;
          }

          return (
            <div>
              <ReactModal isOpen={!!this.state.showAdminControlledIntegrationUpsell}>
                <AdminIntegratedToolsUpsellModal
                  onRequestHide={this.closeUpsellModal}
                  organizationUUID={this.props.organizationUUID}
                  integration={this.state.showAdminControlledIntegrationUpsell}
                />
              </ReactModal>
              <ReactModal isOpen={!!this.state.showPartnerIntegration}>
                <PartnerIntegrationModal
                  onRequestHide={this.closePartnerModal}
                  integration={this.state.showPartnerIntegration}
                />
              </ReactModal>
              <AdminIntegrationsContainer
                fetchPolicy="cache-and-network"
                organizationUUID={this.props.organizationUUID}>
                {({ adminIntegrations, partnerIntegrations }) => (
                  <React.Fragment>
                    <Row size="large">
                      <Heading>Integrate with your LMS</Heading>
                      <Row size="small">
                        <Text lineHeight="large" size="small" color="thunder" element="div">
                          Set up LMS integrations for your entire school or district
                        </Text>
                      </Row>
                      {adminIntegrations.map(integration => (
                        <Row size="xsmall" key={integration.providerKey}>
                          <AdminIntegration
                            canEdit={canEdit}
                            integration={integration}
                            onActionUnauthorized={() => this.showUpsellModal(integration)}
                            organizationUUID={this.props.organizationUUID}
                          />
                        </Row>
                      ))}
                    </Row>
                    <Row Row size="large">
                      <Heading>Learn more about the tools available on Remind</Heading>
                      <Row size="small">
                        <Text lineHeight="large" size="small" color="thunder" element="div">
                          Each of these tools is enabled for your organization. Users can link their
                          accounts to Remind to easily share links with their classes.
                        </Text>
                      </Row>
                      {partnerIntegrations.map(integration => (
                        <PartnerIntegration
                          integration={integration}
                          key={integration.providerKey}
                          organizationUUID={this.props.organizationUUID}
                          onClick={() => this.showPartnerModal(integration)}
                        />
                      ))}
                    </Row>
                  </React.Fragment>
                )}
              </AdminIntegrationsContainer>
            </div>
          );
        }}
      </OrganizationIntegrationPermissions>
    );
  }
}

export default AdminIntegratedToolsView;
